// 🏆 PREMIUM CERTIFICATE EXPORT UTILITIES
// Extracted from admin CertificatesNew.tsx to reuse the exact same beautiful design

// Load ExcelJS library for professional Excel generation with full styling
export const loadExcelJSLibrary = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).ExcelJS) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load ExcelJS library'));
    document.head.appendChild(script);
  });
};

// 🏆 PREMIUM CORPORATE EXCEL: Ultra-Modern Professional Design with Advanced Styling & Data Extraction
export const generateProfessionalExcel = async (certificateData: any, certificateNumber: string) => {
  try {
    // Load ExcelJS library with full styling support
    if (!(window as any).ExcelJS) {
      await loadExcelJSLibrary();
    }

    const ExcelJS = (window as any).ExcelJS;
    
    // Create workbook with ExcelJS (full styling support)
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rapport AA');
    
    // Set worksheet properties with larger cells for better readability
    worksheet.properties.defaultRowHeight = 30;
    worksheet.properties.defaultColWidth = 20;
    
    // 🎨 PREMIUM CORPORATE COLOR SCHEME
    const colors = {
      primary: 'FF2E5984',      // Deep corporate blue
      secondary: 'FF4A90D9',    // Bright accent blue
      accent: 'FFF39C12',       // Professional gold
      success: 'FF27AE60',      // Modern green
      text: 'FF2C3E50',         // Dark professional text
      lightBg: 'FFF8F9FA',     // Ultra-light background
      border: 'FFBDC3C7'       // Subtle border gray
    };
    
    // 🏢 CORPORATE HEADER SECTION WITH LOGO
    const headerRow = worksheet.addRow(['']);
    headerRow.height = 120; // Increased header height for larger logo
    worksheet.mergeCells('A1:H1');
    const headerCell = worksheet.getCell('A1');
    
    // Add AFRILAB logo to header with improved loading
    try {
      // Multiple logo URL attempts for better reliability
      const logoUrls = [
        `${window.location.origin}/logo2.png`,
        `${window.location.origin}/logo.png`,
        '/logo2.png',
        '/logo.png'
      ];
      
      let logoLoaded = false;
      for (const logoUrl of logoUrls) {
        try {
          console.log('Attempting to fetch logo from:', logoUrl);
          
          const logoResponse = await fetch(logoUrl, {
            method: 'GET',
            cache: 'no-cache',
            mode: 'cors'
          });
          
          if (logoResponse.ok) {
            const logoBuffer = await logoResponse.arrayBuffer();
            console.log('Logo loaded successfully from:', logoUrl, 'size:', logoBuffer.byteLength);
            
            const logoImageId = workbook.addImage({
              buffer: logoBuffer,
              extension: 'png',
            });
            
            // Much larger logo size for better visibility in Excel
            worksheet.addImage(logoImageId, {
              tl: { col: 0, row: 0 },
              ext: { width: 240, height: 120 }, // Significantly increased size
              editAs: 'absolute'
            });
            console.log('Logo added to Excel successfully with larger size');
            logoLoaded = true;
            break;
          }
        } catch (urlError) {
          console.warn('Failed to load logo from:', logoUrl, urlError);
          continue;
        }
      }
      
      if (!logoLoaded) {
        throw new Error('All logo URLs failed to load');
      }
    } catch (error) {
      console.error('Could not load logo for Excel export:', error);
      // Enhanced fallback text logo with better styling
      headerCell.value = '🏢 AFRILAB - Laboratoire Africain pour l\'Exploitation Minière et l\'Environnement';
      headerCell.font = { 
        name: 'Calibri', 
        size: 16, 
        bold: true, 
        color: { argb: 'FF2E5984' } 
      };
      headerCell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center' 
      };
    }
    
    // Set header text (will be overridden by fallback if logo fails)
    if (!headerCell.value) {
      headerCell.value = 'AFRILAB - Laboratoire Africain pour l\'Exploitation Minière';
    }
    headerCell.font = { 
      name: 'Calibri', 
      size: 18, 
      bold: true, 
      color: { argb: 'FFFFFFFF' } 
    };
    headerCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colors.primary }
    };
    headerCell.alignment = { 
      vertical: 'middle', 
      horizontal: 'center' 
    };
    headerCell.border = {
      top: { style: 'thick', color: { argb: colors.accent } },
      bottom: { style: 'thick', color: { argb: colors.accent } },
      left: { style: 'thick', color: { argb: colors.accent } },
      right: { style: 'thick', color: { argb: colors.accent } }
    };
    
    // 📋 CERTIFICATE INFO SECTION
    worksheet.addRow(['']);
    const infoStartRow = worksheet.lastRow.number + 1;
    
    const certificateInfo = [
      ['📄 Numéro de Certificat:', certificateNumber || 'N/A'],
      ['🧪 Type d\'Analyse:', certificateData.certificate_type === 'BATCH' ? 'Analyse par Lot' : 'Analyse Individuelle'],
      ['📅 Date de Réception:', certificateData.reception_date ? new Date(certificateData.reception_date).toLocaleDateString('fr-FR') : (certificateData.samples?.[0]?.sample_data?.reception_date ? new Date(certificateData.samples[0].sample_data.reception_date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'))],
      ['⏰ Heure de Génération:', new Date().toLocaleTimeString('fr-FR')],
      ['🔬 Laboratoire:', 'AFRILAB - Maroc']
    ];
    
    certificateInfo.forEach((info, index) => {
      const row = worksheet.addRow(info);
      row.height = 35; // Increased row height for better readability
      
      // Style label cell with larger font
      const labelCell = row.getCell(1);
      labelCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: colors.text } }; // Increased font size
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.lightBg } };
      labelCell.border = {
        top: { style: 'thin', color: { argb: colors.border } },
        bottom: { style: 'thin', color: { argb: colors.border } },
        left: { style: 'medium', color: { argb: colors.secondary } },
        right: { style: 'thin', color: { argb: colors.border } }
      };
      labelCell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left',
        indent: 1 
      };
      
      // Style value cell with larger font
      const valueCell = row.getCell(2);
      valueCell.font = { name: 'Calibri', size: 13, color: { argb: colors.text } }; // Increased font size
      valueCell.border = {
        top: { style: 'thin', color: { argb: colors.border } },
        bottom: { style: 'thin', color: { argb: colors.border } },
        left: { style: 'thin', color: { argb: colors.border } },
        right: { style: 'medium', color: { argb: colors.secondary } }
      };
      valueCell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left',
        indent: 1 
      };
    });
    
    // Add spacing
    worksheet.addRow(['']);
    worksheet.addRow(['']);
    
    // BGM BIS exclusion: remove last sample (BIS duplicate) for BGM clients only
    const isBGMClient = certificateData.client_name === 'BGM';
    if (isBGMClient && certificateData.samples && certificateData.samples.length > 1) {
      certificateData.samples = certificateData.samples.slice(0, -1);
    }

    // 📊 DATA SECTION
    if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
      // BATCH CERTIFICATE - Professional table layout
      const elements = (Array.isArray(certificateData.elements) && certificateData.elements.length > 0)
        ? certificateData.elements
        : [...new Set(
            (certificateData.samples || []).flatMap((s: any) =>
              (s.analysis_results || s.results || []).map((r: any) => r.element)
            ).filter(Boolean)
          )];
      
      // Create dynamic headers with better spacing
      const headers = ['🧪 Lab-Ref', '👤 Client-Ref', ...elements.map((el: string) => `${el}`)];
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 45; // Increased header height
      
      // Style headers with gradient effect and better typography
      headerRow.eachCell((cell, colNumber) => {
        cell.font = { 
          name: 'Calibri', 
          size: 14, // Increased font size
          bold: true, 
          color: { argb: 'FFFFFFFF' } 
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colNumber <= 2 ? colors.primary : colors.secondary }
        };
        cell.alignment = { 
          vertical: 'middle', 
          horizontal: 'center',
          wrapText: true,
          indent: 1
        };
        cell.border = {
          top: { style: 'medium', color: { argb: colors.accent } },
          bottom: { style: 'medium', color: { argb: colors.accent } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      });
      
      // Add data rows with professional styling
      certificateData.samples.forEach((sample: any, sampleIndex: number) => {
        const rowData = [
          sample.lab_ref || 'N/A',
          sample.client_ref || 'N/A',
          ...elements.map((element: string) => {
            const result = sample.results?.find((r: any) => r.element === element);
            if (result) {
              return result.formatted_concentration || result.concentration || '0';
            }
            return 'N/A';
          })
        ];
        
        const dataRow = worksheet.addRow(rowData);
        dataRow.height = 32; // Increased row height for better readability
        
        // Alternating row colors for better readability
        const isEvenRow = sampleIndex % 2 === 0;
        const rowBgColor = isEvenRow ? 'FFFFFFFF' : colors.lightBg;
        
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { 
            name: 'Calibri', 
            size: 12, // Increased font size
            color: { argb: colors.text } 
          };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowBgColor }
          };
          cell.alignment = { 
            vertical: 'middle', 
            horizontal: colNumber <= 2 ? 'left' : 'center',
            indent: colNumber <= 2 ? 1 : 0
          };
          cell.border = {
            top: { style: 'thin', color: { argb: colors.border } },
            bottom: { style: 'thin', color: { argb: colors.border } },
            left: { style: 'thin', color: { argb: colors.border } },
            right: { style: 'thin', color: { argb: colors.border } }
          };
          
          // Highlight important columns with better styling
          if (colNumber <= 2) {
            cell.font = { ...cell.font, bold: true };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: isEvenRow ? 'FFF0F8FF' : 'FFE6F3FF' }
            };
          }
        });
      });
      
    } else {
      // INDIVIDUAL CERTIFICATE - Elegant element list with larger cells
      const headers = ['🧪 Élément', '📊 Concentration', '📏 Unité', '🎯 Limite de Détection'];
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 45; // Increased header height
      
      // Style headers with better typography
      headerRow.eachCell((cell) => {
        cell.font = { 
          name: 'Calibri', 
          size: 14, // Increased font size
          bold: true, 
          color: { argb: 'FFFFFFFF' } 
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colors.primary }
        };
        cell.alignment = { 
          vertical: 'middle', 
          horizontal: 'center',
          wrapText: true,
          indent: 1
        };
        cell.border = {
          top: { style: 'medium', color: { argb: colors.accent } },
          bottom: { style: 'medium', color: { argb: colors.accent } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      });
      
      // Add results with premium styling
      const results = certificateData.results || [];
      results.forEach((result: any, index: number) => {
        const rowData = [
          result.element || 'N/A',
          result.formatted_concentration || result.concentration || '0',
          result.unit || 'N/A',
          (() => {
            const dl = result.detection_limit;
            if (typeof dl === 'number') return dl;
            if (dl && typeof dl === 'object') return dl.limit ?? dl.limitInPercent ?? 'N/A';
            return dl || 'N/A';
          })()
        ];
        
        const dataRow = worksheet.addRow(rowData);
        dataRow.height = 32; // Increased row height for better readability
        
        // Alternating colors for better readability
        const isEvenRow = index % 2 === 0;
        const rowBgColor = isEvenRow ? 'FFFFFFFF' : colors.lightBg;
        
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { 
            name: 'Calibri', 
            size: 12, // Increased font size
            color: { argb: colors.text } 
          };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowBgColor }
          };
          cell.alignment = { 
            vertical: 'middle', 
            horizontal: colNumber === 1 ? 'left' : 'center',
            indent: colNumber === 1 ? 1 : 0
          };
          cell.border = {
            top: { style: 'thin', color: { argb: colors.border } },
            bottom: { style: 'thin', color: { argb: colors.border } },
            left: { style: 'thin', color: { argb: colors.border } },
            right: { style: 'thin', color: { argb: colors.border } }
          };
          
          // Highlight element names with better styling
          if (colNumber === 1) {
            cell.font = { ...cell.font, bold: true };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: isEvenRow ? 'FFF0F8FF' : 'FFE6F3FF' }
            };
          }
        });
      });
    }
    
    // 🏢 PROFESSIONAL FOOTER
    worksheet.addRow(['']);
    worksheet.addRow(['']);
    const footerRow = worksheet.addRow(['']);
    footerRow.height = 40;
    worksheet.mergeCells(`A${footerRow.number}:H${footerRow.number}`);
    const footerCell = worksheet.getCell(`A${footerRow.number}`);
    footerCell.value = '✨ Document généré par AFRILAB - Laboratoire certifié ISO/IEC 17025 ✨';
    footerCell.font = { 
      name: 'Calibri', 
      size: 11, 
      italic: true, 
      color: { argb: colors.text } 
    };
    footerCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: colors.lightBg }
    };
    footerCell.alignment = { 
      vertical: 'middle', 
      horizontal: 'center' 
    };
    footerCell.border = {
      top: { style: 'medium', color: { argb: colors.accent } },
      bottom: { style: 'medium', color: { argb: colors.accent } },
      left: { style: 'medium', color: { argb: colors.accent } },
      right: { style: 'medium', color: { argb: colors.accent } }
    };
    
    // Auto-fit columns for professional appearance with better spacing
    worksheet.columns.forEach((column, index) => {
      if (column.values && column.values.length > 0) {
        const maxLength = Math.max(
          ...column.values.map((v: any) => 
            v ? v.toString().length : 0
          )
        );
        // Larger minimum and maximum widths for better readability
        const minWidth = index <= 1 ? 18 : 15; // Extra width for Lab-Ref and Client-Ref columns
        const maxWidth = index <= 1 ? 35 : 30; // Allow wider columns for important data
        column.width = Math.min(Math.max(maxLength + 4, minWidth), maxWidth); // Added extra padding
      } else {
        // Default width for empty columns
        column.width = 20;
      }
    });
    
    // Generate and download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    if (isBGMClient && certificateData.samples && certificateData.samples.length > 0) {
      const numParts = certificateNumber.split('/');
      const rawNum = (numParts[numParts.length - 1] || '').replace('-BATCH', '');
      const reportNum = String(parseInt(rawNum, 10) || 0).padStart(4, '0');
      const firstExtId = certificateData.samples[0]?.sample_data?.external_id || certificateData.samples[0]?.client_ref || '';
      const lastSample = certificateData.samples[certificateData.samples.length - 1];
      const lastExtId = lastSample?.sample_data?.external_id || lastSample?.client_ref || '';
      const last3 = lastExtId.slice(-3);
      link.download = `${reportNum} BGM ${firstExtId}${last3}.xlsx`;
    } else {
      link.download = `AFRILAB_${certificateNumber}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error generating professional Excel:', error);
    throw new Error('Erreur lors de la génération du fichier Excel professionnel');
  }
};

// 🏆 PREMIUM PDF EXPORT: Ultra-Modern Design with Corporate Styling
export const exportToPremiumPDF = async (certificateId: string, certificateNumber: string, token: string) => {
  try {
    const response = await fetch(`/api/v1/certificates/${certificateId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch certificate');
    }
    
    const htmlContent = await response.text();
    
    // Open premium PDF window with enhanced styling
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      throw new Error('Popup blocked - Please allow popups for this site');
    }
    
    pdfWindow.document.open();
    pdfWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>🏆 PDF Premium - ${certificateNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
            line-height: 1.6;
            color: #2C3E50;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
          }
          
          .pdf-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 20px 60px rgba(0,0,0,0.1);
            border-radius: 15px;
            overflow: hidden;
          }
          
          .pdf-header {
            background: linear-gradient(135deg, #2E5984 0%, #4A90D9 100%);
            color: white;
            padding: 30px;
            text-align: center;
            position: relative;
            overflow: hidden;
          }
          
          .pdf-header::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.05) 10px,
              rgba(255,255,255,0.05) 20px
            );
            animation: slide 20s linear infinite;
          }
          
          @keyframes slide {
            0% { transform: translateX(-50px) translateY(-50px); }
            100% { transform: translateX(50px) translateY(50px); }
          }
          
          .pdf-header h1 {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
            position: relative;
            z-index: 2;
          }
          
          .pdf-header p {
            font-size: 16px;
            opacity: 0.9;
            position: relative;
            z-index: 2;
          }
          
          .pdf-content {
            padding: 40px;
          }
          
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
          }
          
          .spinner {
            width: 60px;
            height: 60px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #4A90D9;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 30px;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .loading-text {
            font-size: 18px;
            font-weight: 600;
            color: #2E5984;
            margin-bottom: 20px;
          }
          
          .instructions {
            background: linear-gradient(135deg, #E8F4FD 0%, #F0F8FF 100%);
            padding: 25px;
            border-radius: 15px;
            border-left: 5px solid #4A90D9;
            margin: 20px 0;
            max-width: 500px;
          }
          
          .instructions h4 {
            color: #2E5984;
            margin-bottom: 15px;
            font-size: 16px;
          }
          
          .instructions ol {
            margin-left: 20px;
            margin-bottom: 15px;
          }
          
          .instructions li {
            margin-bottom: 8px;
            color: #34495E;
          }
          
          .instructions strong {
            color: #2E5984;
          }
          
          .button {
            background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            margin: 10px;
            box-shadow: 0 4px 15px rgba(74, 144, 217, 0.3);
            transition: all 0.3s ease;
          }
          
          .button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(74, 144, 217, 0.4);
          }
          
          .button.secondary {
            background: linear-gradient(135deg, #95A5A6 0%, #7F8C8D 100%);
            box-shadow: 0 4px 15px rgba(149, 165, 166, 0.3);
          }
          
          .button.secondary:hover {
            box-shadow: 0 6px 20px rgba(149, 165, 166, 0.4);
          }
          
          .pdf-footer {
            background: linear-gradient(135deg, #ECF0F1 0%, #D5DBDB 100%);
            padding: 25px;
            text-align: center;
            border-top: 3px solid #4A90D9;
            margin-top: 40px;
          }
          
          .pdf-footer p {
            margin: 8px 0;
            color: #5D6D7E;
            font-size: 14px;
          }
          
          /* Print styles */
          @media print {
            body {
              background: white;
            }
            .pdf-container {
              box-shadow: none;
              border-radius: 0;
            }
            .loading-container {
              display: none;
            }
          }
          
          /* Enhanced table styling */
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          th {
            background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
            font-size: 14px;
          }
          
          td {
            padding: 12px 15px;
            border-bottom: 1px solid #E8F4FD;
            font-size: 13px;
          }
          
          tr:nth-child(even) {
            background-color: #F8FBFF;
          }
          
          tr:hover {
            background-color: #E8F4FD;
          }
        </style>
      </head>
      <body>
        <div class="pdf-container">
          <div class="pdf-header">
            <h1>🏆 AFRILAB - Certificat Premium</h1>
            <p>Laboratoire Africain pour l'Exploitation Minière et l'Environnement</p>
          </div>
          
          <div class="pdf-content">
            <div class="loading-container">
              <div class="spinner"></div>
              <div class="loading-text">🔄 Préparation de votre certificat premium en cours...</div>
              <div class="instructions">
                <h4>📄 Pour sauvegarder en PDF Premium :</h4>
                <ol>
                  <li>Utilisez <strong>Ctrl+P</strong> (ou Cmd+P sur Mac)</li>
                  <li>Sélectionnez <strong>"Enregistrer au format PDF"</strong></li>
                  <li>Choisissez la destination et cliquez <strong>"Enregistrer"</strong></li>
                </ol>
                <p><strong>💡 Avantages Premium :</strong> Design moderne, couleurs professionnelles, références automatiques sur chaque page, et styling corporate élégant.</p>
              </div>
              <button class="button" onclick="window.print()">🖨️ Imprimer/Sauvegarder PDF Premium</button>
              <button class="button secondary" onclick="window.close()">❌ Fermer</button>
            </div>
          </div>
        </div>
        
        <script>
          // Load the actual certificate content
          setTimeout(() => {
            document.querySelector('.loading-container').style.display = 'none';
            document.querySelector('.pdf-content').innerHTML = \`${htmlContent}\`;
          }, 1500);
        </script>
      </body>
      </html>
    `);
    pdfWindow.document.close();
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};
